import dayjs from "dayjs";
import { Timestamp } from "@atgof-firebase/types";
import { PhraseKey, PhraseLookupFunction } from "./phrases";

export const UNKNOWN_LABEL = "??";

export const HOUR_MILLIS = 3600000;

export function initials(name: string | null) {
  if (name) {
    const chs = name.split(/\s+/).map((s) => s?.charAt(0));
    if (!chs.length) return "";
    return (chs[0] + (chs.length > 1 ? chs[chs.length - 1] : "")).toUpperCase();
  }
  return;
}

type SortableToken = { pos: number; len: number } & (
  | { n: number }
  | { ch: string }
  | { s: string }
);

export function tokeniseForSorting(x: string | number) {
  const s = typeof x === "number" ? x.toString() : x;
  let entries: SortableToken[] = [];
  let offset = 0;
  let acc = { pos: 0, len: 0, s: "" };
  let i;
  while ((i = s.substring(offset).search(/[\dA-Z]/)) != -1) {
    acc.len += i;
    acc.s += s.substring(offset, offset + i);
    const rest = s.substring(offset + i);
    let m = rest.match(/^\d+/);
    if (m) {
      if (acc.len) entries.push(acc);
      const len = m[0].length;
      entries.push({ pos: offset + i, len, n: parseInt(m[0]) });
      offset += i + len;
      acc = { pos: offset, len: 0, s: "" };
      continue;
    }
    m = rest.match(/^[A-Z]+/);
    if (m && m[0].length == 1) {
      if (acc.len) entries.push(acc);
      entries.push({ pos: offset + i, len: 1, ch: m[0] });
      offset += i + 1;
      acc = { pos: offset, len: 0, s: "" };
      continue;
    }
    acc.len += 1;
    acc.s += s.charAt(offset);
    offset += i + 1;
  }
  if (offset < s.length) {
    acc.len += s.length - offset;
    acc.s += s.substring(offset);
  }
  if (acc.len) entries.push(acc);
  return entries;
}

export function normalisePotentialNumbering(ss: string[]): string[] {
  const intss = ss.map((s) =>
    tokeniseForSorting(s).filter((token) => "n" in token),
  );
  let maxWidths: number[] = [];
  for (const ints of intss) {
    for (let i = 0; i < ints.length; i++) {
      maxWidths[i] = Math.max(maxWidths[i] ?? 0, ints[i].len);
    }
  }
  maxWidths.reverse();
  const maxIntCount = Math.max(...intss.map((ints) => ints.length));
  return ss.map((s, i) =>
    maxWidths.reduce((s, width, j) => {
      const entry = intss[i][maxIntCount - j - 1];
      if (!entry) return s;
      const { pos, len } = entry;
      return width > len
        ? s.substring(0, pos) + "0".repeat(width - len) + s.substring(pos)
        : s;
    }, s),
  );
}

export function compareTokens(
  a: SortableToken | undefined,
  b: SortableToken | undefined,
) {
  if (a && b) {
    if ("s" in a) {
      if ("s" in b) return a.s.localeCompare(b.s);
      if ("n" in b) return -1;
      return 1;
    }
    if ("n" in a) {
      if ("s" in b) return 1;
      if ("n" in b) return a.n - b.n;
      return 1;
    }
    if ("s" in b || "n" in b) return -1;
    return a.ch.localeCompare(b.ch);
  }
  return a ? 1 : b ? -1 : 0;
}

export function comparePotentiallyNumbered(
  a: string | number | undefined,
  b: string | number | undefined,
) {
  if (a && b) {
    const [tokensA, tokensB] = [a, b].map(tokeniseForSorting);
    for (let i = 0; i < Math.max(tokensA.length, tokensB.length); i++) {
      const res = compareTokens(tokensA[i], tokensB[i]);
      if (res) return res;
    }
    return 0;
  }
  return a ? 1 : b ? -1 : 0;
}

export function idForDisplayName(displayName: string) {
  return displayName.replace(/\//g, "_"); // TODO
}

export function displayNameForId(id: string | undefined) {
  return id?.replace(/_/g, "/"); // TODO
}

export function normaliseSubjectName(name: string | undefined) {
  // TODO Look for occurences of toLocaleUpperCase to refactor existing usages to this
  const s = name?.trim().toLocaleUpperCase();
  return s?.length ? s : undefined;
}

export function formatTime(d: Date | undefined) {
  return d?.toLocaleTimeString() || "";
}

export function formatTimecode(timecode: Timestamp | string | undefined) {
  return typeof timecode === "string"
    ? timecode
    : formatTime(timecode?.toDate());
}

export function formatDate(d: Date | undefined, long: boolean = false) {
  return d ? dayjs(d).format((long ? "ddd " : "") + "D MMM YYYY") : "";
}

export function formatTimestamp(
  t: Timestamp | undefined,
  includeTime: boolean = false,
) {
  const d = t?.toDate();
  return d ? formatDate(d) + (includeTime ? " " + formatTime(d) : "") : "";
}

export function tryParsePositiveInt(s: string | undefined) {
  if (!s?.match(/^\d+$/)) return;
  return parseInt(s);
}

export function scriptPagesDescription(pages: string | number) {
  const n = typeof pages === "string" ? tryParsePositiveInt(pages) : pages;
  if (n === undefined) return;
  const fullPages = Math.floor(n / 8);
  const eighths = n % 8;
  let components: string[] = [];
  if (fullPages) components.push(fullPages.toString());
  if (eighths) components.push(`${eighths}/8`);
  return components.join(" ");
}

export function episodeNumberLabel(path: string) {
  const m = path.match(/[^/]+$/);
  return m ? m[0] : UNKNOWN_LABEL;
}

export function episodeLabel(
  ph: PhraseLookupFunction,
  path: string | undefined,
  short: boolean = false,
  daysRatherThanEpisodes: boolean = false,
) {
  const episode = path ? episodeNumberLabel(path) : undefined;
  return (
    (daysRatherThanEpisodes
      ? short
        ? ""
        : ph("story-day")
      : ph(short ? "ep" : "episode")) +
    (episode ? (daysRatherThanEpisodes && short ? "" : " ") + episode : "")
  );
}

export function countLabel(
  ph: PhraseLookupFunction,
  countPhraseKey: PhraseKey,
  count: number | undefined,
  usePhraseForZero: boolean = true,
) {
  if (count === undefined) return;
  const phraseOrFn = ph(countPhraseKey);
  const countPhrase =
    typeof phraseOrFn === "function"
      ? phraseOrFn({ count: count })
      : phraseOrFn;
  return (
    (usePhraseForZero && count == 0 ? ph("zero-count") : count) +
    " " +
    countPhrase
  );
}

export const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

export function parenthesisedBetween(
  prefix: string | undefined,
  suffix: string | undefined,
) {
  if (!(prefix && suffix)) return false;
  const openI = prefix.lastIndexOf("(");
  if (prefix.lastIndexOf(")") > openI) return false;
  const closeI = suffix.indexOf(")");
  if (closeI < 0) return false;
  const openInSuffixI = suffix.indexOf("(");
  if (openInSuffixI >= 0 && openInSuffixI < closeI) return false;
  return true;
}
